/** @jsx isolateComponent */

import React, { useEffect, useState } from 'react';
import { css } from '@emotion/core';
import { first, last } from 'lodash';
import { Img } from '@zapier/design-system/src/components/display/Img';
import { Colors, Typography } from '@zapier/design-system/src/theme';
import {
  getInitialsFromString,
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type AvatarProps = {
  /**
   * Controls whether `Avatar` is hidden from screenreaders.
   * Useful when it renders alongside redundant content.
   */
  ariaHidden?: boolean;

  /** Indicates whether to render the component as a block-level element. */
  isBlock?: boolean;

  /** Name of user */
  name: string;

  /** Whether or not to lazy load the underlying image. */
  shouldLazilyLoad?: boolean;

  /** Size of the `Avatar` to display */
  size?: number;

  /** URL to image */
  url?: string | null;
};

const getNameInitials = (name: string): string => {
  const initials = getInitialsFromString(name.trim());
  const firstLetter = first(initials);

  if (!firstLetter) {
    return '';
  }

  return initials.length === 1
    ? String(firstLetter)
    : `${firstLetter}${last(initials)}`;
};

const Styles = labelStyles(__filename, {
  root: (props: AvatarProps) => [
    css({
      alignItems: 'center',
      background: Colors.grey300,
      borderRadius: '50%',
      display: 'inline-flex',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative',
    }),
    withModifier(`${props.size}`, {
      height: props.size,
      width: props.size,
    }),
    props.isBlock &&
      withModifier('block', {
        display: 'flex',
      }),
  ],
  image: ({ size }: AvatarProps) => [
    css({
      borderRadius: 'inherit',
      display: 'block',
      position: 'relative',
    }),
    withModifier(String(size), {
      height: size,
      width: size,
    }),
  ],
  initials: ({ size }: AvatarProps) => [
    css({ ...Typography.allCaps700, color: Colors.black }),
    size &&
      withModifier(String(size), {
        fontSize: Math.floor(size * 0.45),
      }),
  ],
});
/**
 * Displays a user's avatar. Falls back to their initials if an invalid
 * image url or no image url is provided.
 */

const Avatar: React.FC<AvatarProps> = props => {
  // Used to fall back to initials if the image fails to load
  const [hasError, setHasError] = useState(false);
  const { name, url, size } = props;
  const initials = name ? getNameInitials(name) : null; // reset hasError if the url changes

  useEffect(() => {
    if (hasError) {
      setHasError(false);
    } // we don't want hasError to trigger this effect
    // eslint-disable-next-line
  }, [props.url]);

  return (
    <div
      aria-hidden={props.ariaHidden}
      aria-label={name}
      css={Styles.root(props)}
      role="img"
    >
      {url && !hasError ? (
        <Img
          alt={name}
          ariaHidden={true}
          css={Styles.image(props)}
          height={size} // Use `undefined` for default prop, `null` to opt out.
          lazyloadOffset={props.shouldLazilyLoad ? undefined : null}
          onError={() => setHasError(true)}
          src={url}
          width={size}
        />
      ) : (
        <span css={Styles.initials(props)}>{initials}</span>
      )}
    </div>
  );
};

Avatar.defaultProps = {
  shouldLazilyLoad: true,
  size: 30,
};

export default Avatar;
